import { useEffect, useMemo, useState } from "react"
import { CheckCircle2, ShieldCheck } from "lucide-react"
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import {
	Button,
	CheckoutForm,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components"
import { api } from "../../system/api.js"
import { getSymbolCurrency } from "../../system/util.js"
import { getStripePromise } from "../../system/stripePromise.js"
import { buildStripeAppearance } from "../../system/stripeAppearance.js"

// The amount is chosen before the server is asked for anything, so the client
// needs its own copy of the bounds. Mirrors Logic\TopUp::MIN_AMOUNT/MAX_AMOUNT
// — these only shape the field; the server re-validates and is the authority.
const MIN_AMOUNT = 10
const MAX_AMOUNT = 2000

const CTA_MOTION = "motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-100"

function formatMoney(symbol, amount) {
	return `${symbol}${Number(amount).toFixed(2)}`
}

function formatDate(input) {
	const date = new Date(String(input).replace(" ", "T"))
	if (Number.isNaN(date.getTime())) return null
	return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
}

/**
 * How much extra AI a top-up buys, as a share of the plan's monthly AI.
 *
 * Plainly amount / plan price, and that is exact rather than approximate: a
 * plan's AI budget and a top-up's are both the same percentage of their price,
 * so the conversion rate cancels out of the ratio. It also means the figure can
 * be shown without disclosing that rate.
 *
 * Only holds for plans priced by percentage. A plan with an absolute budget
 * override would break it — none can be topped up, and a test in
 * UserProductUsageTest pins that.
 */
function conversionPct(amountUsd, planMonthlyPrice) {
	if (!planMonthlyPrice || planMonthlyPrice <= 0) return null
	return Math.round((amountUsd / planMonthlyPrice) * 1000) / 10
}

/**
 * Buy prepaid AI credit.
 *
 * Three stages: choose an amount, pay for it, done. The amount is the user's
 * choice, so unlike every other purchase in the app the server opens a
 * PaymentIntent for it and then re-reads what Stripe actually captured — the
 * figures here are for display, never the source of truth for what is granted.
 */
export default function TopUpDialog({
	open,
	onOpenChange,
	currency = "USD",
	planName = "",
	planMonthlyPrice = 0,
	onSuccess = null,
}) {
	const symbol = getSymbolCurrency(currency)

	const [amount, setAmount] = useState(String(MIN_AMOUNT))
	const [stage, setStage] = useState("amount")
	const [purchased, setPurchased] = useState(null)

	const [checkout, setCheckout] = useState(null)
	const [checkoutLoading, setCheckoutLoading] = useState(false)
	const [checkoutError, setCheckoutError] = useState(null)
	const [stripePromise, setStripePromise] = useState(null)

	// Reset on every open so a cancelled purchase doesn't leave the dialog
	// sitting on someone else's checkout the next time it is opened.
	useEffect(() => {
		if (!open) return
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setAmount(String(MIN_AMOUNT))
		setStage("amount")
		setPurchased(null)
		setCheckout(null)
		setCheckoutError(null)
		setStripePromise(null)
	}, [open])

	const parsedAmount = useMemo(() => {
		const value = Number.parseFloat(amount)
		return Number.isFinite(value) ? Math.round(value * 100) / 100 : NaN
	}, [amount])

	const valid = Number.isFinite(parsedAmount)
		&& parsedAmount >= MIN_AMOUNT
		&& parsedAmount <= MAX_AMOUNT
	const pct = valid ? conversionPct(parsedAmount, planMonthlyPrice) : null

	const startCheckout = async () => {
		setCheckoutLoading(true)
		setCheckoutError(null)

		try {
			const res = await api("topup/initiate", { amount: parsedAmount })
			setCheckout(res)
			setStripePromise(getStripePromise(res.stripePublicKey))
			setStage("checkout")
		} catch (err) {
			setCheckoutError(
				err?.errorCode === "TOPUP_AMOUNT_INVALID"
					? "That amount is outside the allowed range."
					: "Could not start the purchase. Please try again."
			)
		} finally {
			setCheckoutLoading(false)
		}
	}

	const handlePaid = (result) => {
		setPurchased(result)
		setStage("success")
		onSuccess?.()
	}

	const amountLabel = formatMoney(symbol, Number.isFinite(parsedAmount) ? parsedAmount : 0)

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{/* The checkout stage renders a two-column grid, so the dialog
			    widens for it; the amount stage stays compact. */}
			<DialogContent
				className={`v1 bg-card max-h-[90vh] overflow-y-auto p-6 ${
					stage === "checkout" ? "sm:max-w-2xl" : "sm:max-w-md"
				}`}
			>
				<div className="mb-4">
					<DialogTitle className="text-page-title text-foreground">
						{stage === "success" ? "Credit added" : "Top-up credits"}
					</DialogTitle>
					<DialogDescription className="text-caption text-muted-foreground mt-0.5">
						{stage === "success"
							? "Your balance is ready on the Billing page."
							: `Add credit from ${formatMoney(symbol, MIN_AMOUNT)}. It converts to extra AI usage, relative to your plan price.`}
					</DialogDescription>
				</div>

				{stage === "success" ? (
					<div className="animate-in fade-in duration-500 flex flex-col items-center justify-center py-8 px-6 text-center">
						<CheckCircle2 size={48} strokeWidth={1.5} className="text-success mb-4" />
						<p className="text-body text-muted-foreground max-w-sm text-balance">
							{formatMoney(symbol, purchased?.amount ?? parsedAmount)} is on your account.
							It&apos;s consumed only after your monthly allowance runs out
							{purchased?.expiresAt ? `, and expires on ${formatDate(purchased.expiresAt)}` : ""}.
						</p>
						<Button
							size="sm"
							className={`mt-5 text-label ${CTA_MOTION}`}
							onClick={() => onOpenChange(false)}
						>
							Done
						</Button>
					</div>
				) : stage === "checkout" && stripePromise ? (
					<div className="flex flex-col gap-4">
						<button
							type="button"
							onClick={() => setStage("amount")}
							className={`text-caption text-muted-foreground hover:text-foreground cursor-pointer self-start ${CTA_MOTION}`}
						>
							← Change amount
						</button>
						<Elements
							stripe={stripePromise}
							options={{
								clientSecret: checkout.clientSecret,
								appearance: buildStripeAppearance(),
							}}
						>
							<TopUpCheckout
								amount={Number(checkout.amount)}
								currency={checkout.currency}
								symbol={symbol}
								planName={planName}
								planMonthlyPrice={planMonthlyPrice}
								paymentMethods={checkout.paymentMethods || []}
								onPaid={handlePaid}
							/>
						</Elements>
					</div>
				) : (
					<div className="flex flex-col gap-4">
						{/* The one fact that defuses the limit anxiety. Deployed
						    strategies run on the user's own terminal and never call
						    our AI, so they genuinely keep going — but a backtest and
						    an optimizer sweep each start with an AI config call, so
						    those do stop, and credit is what brings them back. */}
						<div className="flex items-start gap-2.5 rounded-md border border-success/30 bg-success/10 px-3.5 py-3">
							<ShieldCheck size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-success" />
							<p className="text-caption text-foreground">
								Deployed strategies keep trading through your AI limit. Backtesting
								and the optimiser need credit — this restores them.
							</p>
						</div>

						{/* Converter — amount in, plan-relative usage out */}
						<div className="rounded-md border border-border bg-muted/40 p-4">
							<div className="flex items-end justify-between gap-4">
								<div className="flex-1">
									<label
										htmlFor="topup-amount"
										className="text-caption text-muted-foreground mb-1 block"
									>
										Amount to add
									</label>
									<div className="relative">
										<span className="absolute top-1/2 left-3 -translate-y-1/2 text-body text-muted-foreground">
											{symbol}
										</span>
										{/* Whole dollars: the spinner arrows follow step, and
										    nudging a top-up by a cent is never what anyone
										    wants. Cents typed by hand still go through — the
										    server rounds to 2dp and charges what it reads. */}
										<input
											id="topup-amount"
											type="number"
											inputMode="decimal"
											min={MIN_AMOUNT}
											max={MAX_AMOUNT}
											step="1"
											value={amount}
											onChange={(e) => setAmount(e.target.value)}
											className="w-full bg-muted border border-border rounded-md pl-7 pr-3 py-2 text-body text-foreground outline-none focus:border-ring/60 transition-colors"
										/>
									</div>
								</div>
								{planName && (
									<div className="text-right pb-0.5">
										<span className="text-caption text-muted-foreground block">
											Your plan
										</span>
										<span className="text-body text-foreground font-medium">
											{planName}
										</span>
									</div>
								)}
							</div>

							<div className="mt-4 border-t border-border pt-4 text-center">
								{!valid ? (
									<p className="text-caption text-muted-foreground">
										{Number.isFinite(parsedAmount) && parsedAmount > MAX_AMOUNT
											? `Maximum top-up is ${formatMoney(symbol, MAX_AMOUNT)}.`
											: `Minimum top-up is ${formatMoney(symbol, MIN_AMOUNT)}.`}
									</p>
								) : pct != null ? (
									<>
										<p className="text-page-title text-foreground font-semibold tabular-nums">
											+{pct}%
										</p>
										<p className="text-caption text-muted-foreground mt-0.5">
											additional usage of your {planName} plan
										</p>
									</>
								) : (
									// No plan price to compare against — show the amount
									// rather than an invented ratio.
									<p className="text-caption text-muted-foreground">
										{amountLabel} of additional AI usage.
									</p>
								)}
							</div>
						</div>

						<p className="text-caption text-muted-foreground">
							Consumed after your monthly allowance, oldest credit first. Expires one
							year after purchase and does not renew.
						</p>

						{checkoutError && <p className="text-caption text-danger">{checkoutError}</p>}

						<Button
							className={`w-full text-label ${CTA_MOTION}`}
							disabled={!valid || checkoutLoading}
							onClick={startCheckout}
						>
							{checkoutLoading ? "Loading…" : "Continue to payment"}
						</Button>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}

/**
 * The order the checkout chrome renders. Everything here is face value — the
 * share of the price that becomes provider spend never appears.
 */
function buildTopUpOrder({ amount, symbol, planName, planMonthlyPrice }) {
	const price = formatMoney(symbol, amount)
	const pct = conversionPct(amount, planMonthlyPrice)

	return {
		summaryTitle: "Top-up credit",
		summaryRows: [
			{ label: "Credit:", value: price },
			...(pct != null && planName ? [{ label: "Adds:", value: `+${pct}% of ${planName} AI` }] : []),
			{ label: "Billing:", value: "One-time charge" },
		],
		dueTodayLabel: "Due today:",
		dueTodayPrice: price,
		nextBilling: null,
		benefits: [
			"Consumed only after your monthly allowance",
			"Stays on your account for a year",
			"Restores backtesting and the optimiser at your AI limit",
		],
		disclaimer:
			"By providing your card information, you allow AlgoBuilder to charge your card for this one-time top-up in accordance with our terms.",
		ctaLabel: `Add ${price} credit`,
		footer: `You'll be charged ${price} today. Credit is used after your monthly allowance.`,
	}
}

function TopUpCheckout({ amount, currency, symbol, planName, planMonthlyPrice, paymentMethods, onPaid }) {
	const stripe = useStripe()
	const elements = useElements()

	const [processing, setProcessing] = useState(false)
	const [error, setError] = useState(null)
	const [selectedMethod, setSelectedMethod] = useState(
		paymentMethods.length > 0 ? paymentMethods[0].id : "new"
	)

	const handleSubmit = async () => {
		if (!stripe || !elements) return

		setProcessing(true)
		setError(null)

		try {
			let paymentData = {}

			if (selectedMethod === "new") {
				const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
					elements,
					confirmParams: { return_url: window.location.origin + "/billing" },
					redirect: "if_required",
				})

				if (confirmError) {
					setError(
						confirmError.type === "card_error" || confirmError.type === "validation_error"
							? confirmError.message
							: "The payment could not be processed. Please try again."
					)
					setProcessing(false)
					return
				}

				if (paymentIntent?.status !== "succeeded") {
					setError("The payment was not successful. Please try again.")
					setProcessing(false)
					return
				}

				paymentData = { paymentIntentId: paymentIntent.id }
			} else {
				paymentData = { paymentMethodId: selectedMethod }
			}

			const result = await api("topup/confirm", { amount, paymentData })
			onPaid(result)
		} catch (err) {
			setError(err?.message || "Payment failed. Please try again.")
			setProcessing(false)
		}
	}

	const paymentMethodOptions = [
		...paymentMethods.map((m) => ({
			value: m.id,
			label: `${m.brand.charAt(0).toUpperCase() + m.brand.slice(1)} ending in ${m.last4} (Exp: ${String(m.exp_month).padStart(2, "0")}/${m.exp_year})`,
		})),
		{ value: "new", label: "Use a new card" },
	]

	return (
		<CheckoutForm
			variant="modal"
			processing={processing}
			processingLabel="Processing…"
			onSubmit={handleSubmit}
			order={buildTopUpOrder({ amount, symbol, planName, planMonthlyPrice })}
			secureCaption={`A one-time charge in ${currency}. Your card is never stored.`}
			paymentSlot={
				<div className="flex flex-col gap-3">
					{paymentMethods.length > 0 && (
						<div>
							<label className="text-caption text-muted-foreground mb-1 block">
								Payment method
							</label>
							<Select value={selectedMethod} onValueChange={setSelectedMethod}>
								<SelectTrigger className="w-full bg-muted border-border text-body">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{paymentMethodOptions.map((opt) => (
										<SelectItem key={opt.value} value={opt.value} className="text-body">
											{opt.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{selectedMethod === "new" && <PaymentElement options={{ layout: "tabs" }} />}

					{error && <p className="text-caption text-danger">{error}</p>}
				</div>
			}
		/>
	)
}
